/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { useMemo, useRef } from 'react'
import styled from 'styled-components'
import { Field, Form, Formik, FormikProps } from 'formik'
import * as Yup from 'yup'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Anchor, Card, Form as NXUIForm } from '@nexigendigital/nxui'
import { domainAPI, useUpdateNameserversMutation } from 'api/domain'
import { DNSModeType } from 'models/enums'

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function AddNameserverForm() {
    const addNameserverFormRef = useRef<FormikProps<{ nameserver: string }>>(null)
    const id = +location.pathname?.split('/')[2]
    const { data: { nameservers } = { nameservers: [] }, isError } = domainAPI.endpoints.dnsRecords.useQueryState(id)
    const [updateNameservers, { isLoading }] = useUpdateNameserversMutation()

    const column = useMemo(() => {
        if (isError) {
            return []
        }

        return [
            {
                Header: 'Custom Nameservers',
                accessor: 'nameservers'
            },
            {
                Header: 'Action',
                accessor: 'action',
                align: 'right'
            }
        ]
    }, [])
    const data = useMemo(() => {
        if (isError) {
            return []
        }

        return [{
            nameservers: (
                <Field
                    label={'Add Nameserver'}
                    name={'nameserver'}
                    type={'text'}
                    component={NXUIForm.InputField}
                />
            ),
            action: (
                <Anchor color={'primary'} loading={isLoading && 'Adding'} type={'submit'} onClick={() => {
                    if (addNameserverFormRef.current) {
                        addNameserverFormRef.current.submitForm()
                        addNameserverFormRef.current.setSubmitting(false)
                    }
                }}>
                    Add
                </Anchor>
            )
        }]
    }, [isLoading])

    return (
        <Formik
            initialValues={{
                nameserver: ''
            }}
            validationSchema={
                Yup.object().shape({
                    nameserver: Yup.string().required('Required')
                })
            }
            innerRef={addNameserverFormRef}
            enableReinitialize={true}
            onSubmit={({ nameserver }, { setSubmitting, resetForm }) => {
                updateNameservers({
                    id,
                    dns_config: DNSModeType.CustomNameservers,
                    nameservers: [...nameservers, nameserver]
                })
                setSubmitting(false)
                resetForm()
            }}
        >
            {() => (
                <FormWrapper>
                    <Card
                        table={{
                            render: {
                                columns: () => column,
                                data: () => data
                            },
                            conditions: {
                                header: false,
                                search: false,
                                pagination: false
                            }
                        }}
                    />
                </FormWrapper>
            )}
        </Formik>
    )
}

/**********************************************************************************************************
 *   CUSTOM STYLING
 **********************************************************************************************************/
const FormWrapper = styled(Form)`
    border-top: 1px solid ${props => (props.theme['gray--100'])};
    
    article {
        padding-top: 22px;
    }
`
